import { StreamingIndicator } from "@/components/Chat/StreamingIndicator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { dropdownMenuItemDestructiveClasses } from "@/lib/constants/menu-styles";
import {
  Bookmark,
  Copy,
  // Link, // Temporarily unused - share functionality disabled
  MoreHorizontal,
  Pencil,
  Trash,
} from "lucide-react";
import PropTypes from "prop-types";
import { useEffect } from "react";

export function ThreadItem({
  thread,
  index,
  workspace,
  firstMessage,
  isDeleting,
  isRenaming,
  isSelected,
  openDropdownId,
  user,
  newTitle,
  inputRef,
  onThreadSelect,
  onOpenDropdownChange,
  onToggleBookmark,
  onStartRenaming,
  onRename,
  onCancelRename,
  onTitleChange,
  onShare,
  onClone,
  onDelete,
}) {
  // Focus input when renaming starts
  useEffect(() => {
    if (isRenaming && isSelected && inputRef?.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 150);
    }
  }, [isRenaming, isSelected, inputRef]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      localStorage.setItem("workspaceThreadContext", workspace.id);
      localStorage.setItem(
        "isSharedThread",
        thread.metadata?.is_shared_thread === true ? "true" : "false"
      );
      onThreadSelect(thread);
    }
  };

  const handleClick = (e) => {
    // Don't navigate if the click came from dropdown menu
    if (
      e.target.closest(".dropdown-menu-item") ||
      (isRenaming && isSelected)
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    localStorage.setItem("workspaceThreadContext", workspace.id);
    localStorage.setItem(
      "isSharedThread",
      thread.metadata?.is_shared_thread === true ? "true" : "false"
    );
    onThreadSelect(thread);
  };

  const handleRenameSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (newTitle?.trim()) {
      onRename();
    }
  };

  const handleInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter" && newTitle?.trim()) {
      e.preventDefault();
      onRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancelRename();
    }
  };

  const handleInputBlur = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const currentTitle = newTitle?.trim();
    setTimeout(() => {
      if (isRenaming && isSelected) {
        if (currentTitle) {
          onRename();
        } else {
          onCancelRename();
        }
      }
    }, 500);
  };

  return (
    <div
      key={thread.id}
      tabIndex={0}
      role="button"
      onKeyDown={handleKeyDown}
      className={cn(
        "group/thread flex items-center w-full p-3 bg-transparent hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-all duration-200 cursor-pointer rounded-2xl thread-drag-item focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:relative focus-visible:z-10 outline-none",
        index > 0 && "border-t border-gray-100 dark:border-zinc-800/50",
        isDeleting && "opacity-50 pointer-events-none animate-pulse"
      )}
      onClick={handleClick}
    >
      <div className="flex-1 min-w-0">
        {isRenaming && isSelected ? (
          <div
            className="w-full"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <form className="w-full" onSubmit={handleRenameSubmit}>
              <Input
                id={`rename-input-${thread.id}`}
                ref={inputRef}
                value={newTitle || ""}
                onChange={(e) => {
                  e.stopPropagation();
                  onTitleChange(e.target.value);
                }}
                placeholder="Enter thread name"
                className="h-6 py-0 px-1 text-sm w-full"
                onKeyDown={handleInputKeyDown}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onFocus={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onBlur={handleInputBlur}
              />
            </form>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-gray-800 dark:text-white hover:text-gray-900 dark:hover:text-gray-100 transition-colors line-clamp-1 text-sm">
                {thread.title || "Untitled Chat"}
              </h3>
              <StreamingIndicator threadId={thread.id} />
            </div>
            {firstMessage && (
              <p className="text-xs text-gray-500 dark:text-zinc-400 line-clamp-1">
                {firstMessage.content}
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {thread.metadata?.created_by && thread.metadata.created_by !== user?.id && (
          <span className="text-[10px] text-gray-600 dark:text-zinc-400 bg-gray-100 dark:bg-zinc-800/80 px-1.5 py-0.5 rounded-full">
            shared
          </span>
        )}

        <DropdownMenu
          open={openDropdownId === thread.id}
          onOpenChange={(open) => {
            onOpenDropdownChange(open ? thread.id : null);
          }}
        >
          <DropdownMenuTrigger asChild>
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className="h-6 w-6 flex items-center justify-center bg-transparent rounded-sm opacity-0 group-hover/thread:opacity-100 group-focus-within/thread:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:z-10 outline-none transition-opacity duration-200 hover:bg-gray-100 dark:hover:bg-zinc-700 cursor-pointer flex-shrink-0"
            >
              <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="right"
            alignOffset={-5}
            sideOffset={5}
            className="w-[160px] z-[9999] rounded-lg bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800"
            style={{ zIndex: 9999 }}
            forceMount
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              className="flex cursor-pointer items-center dropdown-menu-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleBookmark(thread);
              }}
            >
              <Bookmark className="h-4 w-4 mr-2" />
              {thread.metadata?.bookmarked ? "Remove" : "Bookmark"}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex cursor-pointer items-center dropdown-menu-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onStartRenaming(thread);
              }}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
{/* Share as link - temporarily disabled
            <DropdownMenuItem
              className="flex cursor-pointer items-center dropdown-menu-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onShare(thread);
              }}
            >
              <Link className="h-4 w-4 mr-2" />
              Share as link
            </DropdownMenuItem>
*/}
            <DropdownMenuItem
              className="flex cursor-pointer items-center dropdown-menu-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClone(thread);
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Clone
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn(
                "flex cursor-pointer items-center dropdown-menu-item",
                dropdownMenuItemDestructiveClasses
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(thread);
              }}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

ThreadItem.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  index: PropTypes.number.isRequired,
  workspace: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  firstMessage: PropTypes.shape({
    content: PropTypes.string,
  }),
  isDeleting: PropTypes.bool,
  isRenaming: PropTypes.bool,
  isSelected: PropTypes.bool,
  openDropdownId: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  newTitle: PropTypes.string,
  inputRef: PropTypes.object,
  onThreadSelect: PropTypes.func.isRequired,
  onOpenDropdownChange: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
  onStartRenaming: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onCancelRename: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  onShare: PropTypes.func, // Optional - share functionality temporarily disabled
  onClone: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ThreadItem;
